/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Row } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { Button, Select, FieldGroup } from "core/components";
import { InventoryItemFormData, ProjectFormData } from "core/schema";
import { Option } from "core/types";
import ProjectParticularsContext from "./ProjectParticularsContext";

type ProjectParticularFormProps = {
  index: number;
  remove: () => void;
  project: ProjectFormData;
  inventory_items: Array<InventoryItemFormData>;
  particular_types: Array<Option>;
};

const ProjectParticularForm = ({ index, remove, project, inventory_items, particular_types, isLoading }: ProjectParticularFormProps): JSX.Element => {
  const requiredParticularCount = 3;
  const { control, formState, setValue, watch } = useFormContext();
  const fieldItemName = `project_tasks_attributes.[0].project_task_items_attributes.[${index}]`;
  const [selectedItem, setSelectedItem] = React.useState(
    inventory_items.find(
      (inventory_item) => inventory_item.id == project?.project_tasks_attributes?.[0]?.project_task_items_attributes?.[index]?.item_id
    )
  );
  const { selectedItems, setSelectedItems } = React.useContext(ProjectParticularsContext);
  const errors = formState.errors?.project_tasks_attributes?.[0]?.project_task_items_attributes?.[index];
  const budgeted_quantity = watch(`${fieldItemName}.budgeted_quantity`);
  const budgeted_unit_rate = watch(`${fieldItemName}.budgeted_unit_rate`);

  const calculateUnitPrice = (quantity: number, unit_rate: number): number => {
    if (unit_rate <= 0 || quantity <= 0) {
      return 0;
    } else {
      return quantity * unit_rate;
    }
  };

  const addToSelectedItems = (item) => {
    if (selectedItems.find((selectedItem) => selectedItem?.id == item?.id)) {
      return;
    } else {
      setSelectedItems((value) => [...value, item]);
    }
  };

  const removeFromSelectedItems = (item) => {
    if (selectedItems.find((selectedItem) => selectedItem?.id == item?.id)) {
      setSelectedItems(selectedItems.filter((selectedItem) => selectedItem?.id != item.id) || []);
    }
  };

  const canRemove = () => {
    return index > requiredParticularCount && !project.id;
  };

  React.useEffect(() => {
    if (selectedItem) {
      addToSelectedItems(selectedItem);
    }
  }, [selectedItems]);

  return (
    <Row className="align-items-center">
      <Col>
        <Controller
          name={`${fieldItemName}.variant`}
          control={control}
          render={({ field }) => (
            <Select
              label="Type"
              placeholder="Select a Type"
              noOptionsMessage={() => "No particular types."}
              options={particular_types}
              isClearable={false}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              onChange={(option) => {
                field.onChange(option?.value);
              }}
              ref={field.ref}
              value={particular_types.find((particular_type) => particular_type.value == field.value) || null}
              errorMessage={errors?.variant?.message}
              disabled={!!project?.project_tasks_attributes?.[0]?.project_task_items_attributes?.[index]?.variant}
            />
          )}
        />
      </Col>
      <Col lg={3}>
        <Controller
          name={`${fieldItemName}.item_id`}
          control={control}
          render={({ field }) => (
            <Select
              label="Inventory Item"
              isLoading={isLoading}
              placeholder="Select an Inventory Item"
              noOptionsMessage={() => "No available inventory items."}
              options={inventory_items.filter(
                (inventory_item) =>
                  !selectedItems.find((selected) => selected?.id == inventory_item?.id && field?.value != inventory_item?.id) || null
              )}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              getOptionValue={(option) => option.id}
              isOptionDisabled={(option) => !option.active}
              onChange={(option, actionMeta) => {
                field.onChange(option?.id);

                if (selectedItem) {
                  removeFromSelectedItems(selectedItem);
                }

                setSelectedItem(option);

                if (actionMeta.action == "select-option") {
                  setValue(`${fieldItemName}.unit_measure_id`, option?.unit_measure_id);
                  setValue(`${fieldItemName}.budgeted_unit_rate`, option?.unit_rate);
                  addToSelectedItems(option);
                } else {
                  setValue(`${fieldItemName}.unit_measure_id`, null);
                  setValue(`${fieldItemName}.budgeted_unit_rate`, "");
                  removeFromSelectedItems(option || actionMeta?.removedValues[0]);
                }
              }}
              ref={field.ref}
              value={inventory_items.find((inventory_item) => inventory_item.id == field.value) || null}
              errorMessage={errors?.item_id?.message}
            />
          )}
        />
      </Col>
      <Col>
        <Controller
          name={`${fieldItemName}.budgeted_quantity`}
          control={control}
          render={({ field }) => (
            <FieldGroup label="Budgeted Quantity" errorMessage={errors?.budgeted_quantity?.message}>
              <div className="d-flex">
                <IMaskInput
                  mask={Number}
                  padFractionalZeros={true}
                  thousandsSeparator=","
                  radix="."
                  value={field.value?.toString()}
                  unmask={true}
                  inputRef={field.ref}
                  onAccept={(value) => {
                    field.onChange(value);
                  }}
                  className="field"
                  placeholder="1000.00"
                  min={0}
                  max={9999999}
                />
                <span
                  className="field"
                  style={{
                    flex: 1,
                    alignItems: "center",
                    fontWeight: "bold",
                    backgroundColor: "#e9ecef",
                    border: "1px solid #ced4da",
                    whiteSpace: "nowrap",
                    paddingTop: "10px",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                >
                  {selectedItem?.unit_measure.code || "UoM"}
                </span>
              </div>
            </FieldGroup>
          )}
        />
      </Col>
      <Col>
        <Controller
          name={`${fieldItemName}.budgeted_unit_rate`}
          control={control}
          render={({ field }) => (
            <FieldGroup label="Budgeted Unit Rate">
              <div className="d-flex">
                <span
                  className="field"
                  style={{
                    flex: 1,
                    alignItems: "center",
                    fontWeight: "bold",
                    backgroundColor: "#e9ecef",
                    border: "1px solid #ced4da",
                    whiteSpace: "nowrap",
                    paddingTop: "10px",
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0
                  }}
                >
                  ₱
                </span>
                <IMaskInput
                  mask={Number}
                  padFractionalZeros={true}
                  thousandsSeparator=","
                  radix="."
                  value={field.value?.toString()}
                  unmask={true}
                  inputRef={field.ref}
                  onAccept={(value) => {
                    field.onChange(value);
                  }}
                  className="field"
                  placeholder="N/A"
                  min={0}
                  max={9999999}
                  readOnly={true}
                />
              </div>
            </FieldGroup>
          )}
        />
      </Col>
      <Col>
        <FieldGroup label="Budgeted Amount">
          <div className="d-flex">
            <span
              className="field"
              style={{
                flex: 1,
                alignItems: "center",
                fontWeight: "bold",
                backgroundColor: "#e9ecef",
                border: "1px solid #ced4da",
                whiteSpace: "nowrap",
                paddingTop: "10px",
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0
              }}
            >
              ₱
            </span>
            <IMaskInput
              mask={Number}
              padFractionalZeros={true}
              thousandsSeparator=","
              radix="."
              unmask={true}
              className="field"
              placeholder="N/A"
              readOnly={true}
              value={calculateUnitPrice(parseFloat(budgeted_quantity), parseFloat(budgeted_unit_rate))?.toString()}
            />
          </div>
        </FieldGroup>
      </Col>
      <Col sm={1}>
        <div className="d-flex justify-content-end align-content-end">
          {canRemove() && (
            <Button
              className="button--danger mr-2"
              icon="minus"
              onClick={() => {
                remove(index);
                removeFromSelectedItems(selectedItem);
              }}
            />
          )}
        </div>
      </Col>
    </Row>
  );
};

export default ProjectParticularForm;
